import React from 'react'

const Jva_dma = () => {
    return (
        <div className='container'>
            <h1>DMA-Dynamic Memory Location</h1>
            <div id="main" class="flex-col clearfix" role="main">
      
      <article id="post-36288"
            class="clearfix post-36288 post type-post status-publish format-standard has-post-thumbnail hentry category-java tag-core-java"
            role="article" itemscope="" itemtype="http://schema.org/BlogPosting">
           
            
                 <h2>Introduction</h2>
                  <div class="bd-anchor" id="introduction"></div>
                  <p>To run an application in an optimal way, JVM divides memory into stack and heap memory.
                        <strong>Whenever we declare new variables and objects, call new method, declare a
                              <em>String</em> or perform similar operations, JVM designates memory to these operations
                              from either Stack Memory or Heap Space.</strong></p>
                  <p>In this tutorial, we'll discuss these memory models. We'll enlist some key differences between
                        them, how they are stored in RAM, the features they offer and where to use them.</p>
                  <h2>Stack memory in java</h2>
                  <div class="bd-anchor" id="stack-memory-in-java"></div>
                  <p><strong>Stack Memory in Java is used for static memory allocation and the execution of a
                              thread.</strong> It contains primitive values that are specific to a method and references
                        to objects that are in a heap, referred from the method.</p>
                  <p>Access to this memory is in Last-In-First-Out (LIFO) order. Whenever a new method is called, a new
                        block on top of the stack is created which contains values specific to that method, like
                        primitive variables and references to objects.</p>
                  
                  <p>When the method finishes execution, it’s corresponding stack frame is flushed, the flow goes back
                        to the calling method and space becomes available for the next method.</p>
                  <h2>Key Features of Stack Memory</h2>
                  <div class="bd-anchor" id="1-key-features-of-stack-memory"></div>
                  <p>Apart from what we have discussed so far, following are some other features of stack memory:</p>
                  <ul>
                        <li>It grows and shrinks as new methods are called and returned respectively</li>
                        <li>Variables inside stack exist only as long as the method that created them is running</li>
                        <li>It's automatically allocated and deallocated when method finishes execution</li>
                        <li>If this memory is full, Java throws <em>java.lang.StackOverFlowError</em></li>
                        <li>Access to this memory is fast when compared to heap memory</li>
                        <li>This memory is threadsafe as each thread operates in its own stack</li>
                  </ul>
                 <h2>Heap space in Java</h2>
                  <div class="bd-anchor" id="heap-space-in-java"></div>
                  <p><strong>Heap space in Java is used for dynamic memory allocation for Java objects and JRE classes
                              at the runtime</strong>. New objects are always created in heap space and the references
                        to this objects are stored in stack memory.</p>
                  <p>These objects have global access and can be accessed from anywhere in the application.</p>
                  <p>This memory model is further broken into smaller parts called generations, these are:</p>
                 
                  <ol>
                        <li><strong>Young Generation – </strong>this is where all new objects are allocated and aged. A
                              minor Garbage collection occurs when this fills up</li>
                        <li><strong>Old or Tenured Generation – </strong>this is where long surviving objects are
                              stored. When objects are stored in the Young Generation, a threshold for the object's age
                              is set and when that threshold is reached, the object is moved to the old generation</li>
                        <li><strong>Permanent Generation – </strong>this consists of JVM metadata for the runtime
                              classes and application methods</li>
                  </ol>
                  <p>These different portions are also discussed in this article –&nbsp;<a
                              href="/jvm-vs-jre-vs-jdk">Difference Between JVM, JRE, and JDK.</a></p>
                  <p>We can always manipulate the size of heap memory as per our requirement.</p>
                  <h3>Key features of java Heap memory</h3>
                  <div class="bd-anchor" id="1-key-features-of-java-heap-memory"></div>
                  <p>Apart from what we have discussed so far, following are some other features of heap space:</p>
                  <ul>
                        <li>It's accessed via complex memory management techniques that include&nbsp;Young Generation,
                              Old or Tenured Generation, and Permanent Generation</li>
                        <li>If heap space is full, Java throws&nbsp;<em>java.lang.OutOfMemoryError</em></li>
                        <li>Access to this memory is relatively slower than stack memory</li>
                        <li>This memory, in contrast to stack, isn't automatically deallocated. It needs Garbage
                              Collector to free up unused objects so as to keep the efficiency of the memory usage</li>
                        <li>Unlike stack, a heap isn't threadsafe and needs to be guarded by properly synchronizing the
                              code</li>
                  </ul>
                 <h2>Example</h2>
                  <p>Based on what we've learned so far, let's analyze a simple Java code and let's assess how memory is <br />
                        managed here:</p> <br />
                  <div className='containers'><code class="language-java hljs"><span class="hljs-class"><span class="keywords">class</span> <span class="hljs-title">Person</span> </span>&#123; <br />
          <span class="keywords">int</span> id; <br />
          String name; <br />
       <br />
          <span class="hljs-function"><span class="keywords">public</span> <span class="hljs-title">Person</span><span class="hljs-params">(<span class="keywords">int</span> id, String name)</span> </span>&#123; <br />
              <span class="keywords">this</span>.id = id; <br />
              <span class="keywords">this</span>.name = name; <br />
          &#125; <br />
      &#125; <br />
       <br />
      <span class="keywords">public</span> <span class="hljs-class"><span class="keywords">class</span> <span class="hljs-title">PersonBuilder</span> </span>&#123; <br />
          <span class="hljs-function"><span class="keywords">private</span> <span class="keywords">static</span> Person <span class="hljs-title">buildPerson</span><span class="hljs-params">(<span class="keywords">int</span> id, String name)</span> </span>&#123; <br />
              <span class="keywords">return</span> <span class="keywords">new</span> Person(id, name); <br />
          &#125; <br />
       <br />
          <span class="hljs-function"><span class="keywords">public</span> <span class="keywords">static</span> <span class="keywords">void</span> <span class="hljs-title">main</span><span class="hljs-params">(String[] args)</span> </span>&#123; <br />
              <span class="keywords">int</span> id = <span class="hljs-number">23</span>; <br />
              String name = <span class="hljs-string">"John"</span>; <br />
              Person person = <span class="keywords">null</span>; <br />
              person = buildPerson(id, name); <br />
          &#125; <br />
      &#125;</code></div> <br />
                  <p>Let's analyze this step-by-step:</p>
                 
                  <ol>
                        <li>Upon entering the <em>main()</em> method, a space in stack memory would be created to store
                              primitives and references of this method
                              <ul>
                                    <li>The primitive value of integer&nbsp;<em>id</em> will be stored directly in stack
                                          memory</li>
                                    <li>The reference&nbsp;variable <em>person&nbsp;</em>of type
                                          <em>Person&nbsp;</em>will also be created in stack memory which will point to
                                          the actual object in the heap</li>
                              </ul>
                        </li>
                        <li>The call to the parameterized constructor&nbsp;<em>Person(int, String)</em> from
                              <em>main()</em>&nbsp;will allocate further memory on top of the previous stack. This will
                              store:
                              <ul>
                                    <li>The <em>this</em> object reference of the calling object in stack memory</li>
                                    <li>The primitive value&nbsp;<em>id&nbsp;</em>in the stack memory</li>
                                    <li>The reference variable of <em>String</em> argument <em>name</em> which will
                                          point to the actual string from string pool in heap memory</li>
                              </ul>
                        </li>
                        <li>The <em>main</em> method is further calling the <em>buildPerson()</em> static method, for
                              which further allocation will take place in stack memory on top of the previous one. This
                              will again store variables in the manner described above.</li>
                        <li>However, for the newly created object&nbsp;<em>person </em>of type&nbsp;<em>Person</em>, all
                              instance variables will be stored in heap memory.</li>
                  </ol>
                 <h2>Summary</h2>
                  <div class="bd-anchor" id="summary"></div>
                  <p>Before we conclude this article, let's quickly summarize the differences between the Stack Memory
                        and the Heap Space:</p>
                  <table>
                        <thead >
                              <tr>
                                    <th style={{width: '25%'}}>Parameter</th>
                                    <th style={{width: '30%'}}>Stack Memory</th>
                                    <th style={{width: '45%'}}>Heap Space</th>
                              </tr>
                        </thead>
                        <tbody>
                              <tr>
                                    <td>Application</td>
                                    <td>Stack is used in parts, one at a time during execution of a thread</td>
                                    <td>The entire application uses Heap space during runtime</td>
                              </tr>
                              <tr>
                                    <td>Size</td>
                                    <td>Stack has size limits depending upon OS and is usually smaller then Heap</td>
                                    <td>There is no size limit on Heap</td>
                              </tr>
                              <tr>
                                    <td>Storage</td>
                                    <td>Stores only primitive variables and references to objects that are created in
                                          Heap Space</td>
                                    <td>All the newly created objects are stored here</td>
                              </tr>
                              <tr>
                                    <td>Order</td>
                                    <td>It is accessed using Last-in First-out (LIFO) memory allocation system</td>
                                    <td>This memory is accessed via complex memory management techniques that
                                          include&nbsp;Young Generation, Old or Tenured Generation, and Permanent
                                          Generation.</td>
                              </tr>
                              <tr>
                                    <td>Life</td>
                                    <td>Stack memory only exists as long as the current method is running</td>
                                    <td>Heap space exists as long as the application runs</td>
                              </tr>
                              <tr>
                                    <td>Efficiency</td>
                                    <td>Comparatively much faster to allocate when compared to heap</td>
                                    <td>Slower to allocate when compared to stack</td>
                              </tr>
                              <tr>
                                    <td>Allocation/Deallocation</td>
                                    <td>This Memory is automatically allocated and deallocated when a method is called
                                          and returned respectively</td>
                                    <td>Heap space is allocated when new objects are created and deallocated by Gargabe
                                          Collector when they are no longer referenced</td>
                              </tr>
                        </tbody>
                  </table>
                  <p>Stack and heap are two ways in which Java allocates memory. In this article, we understood how they
                        work and when to use them for developing better Java programs.</p>
      
            
      </article>
      
</div>

        </div>
    )
}

export default Jva_dma
